/**
 * Whether the tab is visible, and a way to hear when that changes (NG-2816).
 *
 * The one place a poll asks "should I request now?". A hidden tab (a
 * background tab, or a fully covered window on macOS) should not keep asking
 * the server: nobody sees the answer, and every open tab adds load. React code
 * reads it through `useDocumentVisible`; a store poll calls these directly.
 *
 * Where there is no `document` (the prerendered shell) the page counts as
 * visible, so code that runs there behaves as it always did.
 */
export function isDocumentVisible(): boolean {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

/** Call `onChange` on every visibility change; returns the function that stops it. */
export function subscribeToDocumentVisibility(onChange: () => void): () => void {
  if (typeof document === "undefined") return () => {};
  document.addEventListener("visibilitychange", onChange);
  return () => document.removeEventListener("visibilitychange", onChange);
}

/**
 * Resolves now if the tab is visible, else the next time it is shown. For a
 * poll written as a loop rather than an effect: `await` it before each read,
 * and a hidden tab sends nothing until it is back.
 */
export function whenDocumentVisible(): Promise<void> {
  if (isDocumentVisible()) return Promise.resolve();
  return new Promise((resolve) => {
    const stop = subscribeToDocumentVisibility(() => {
      if (!isDocumentVisible()) return;
      stop();
      resolve();
    });
  });
}
